import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import ContentRichTextEditor from '../../components/admin/ContentRichTextEditor';

type HomeHeroConfig = {
  kicker: string;
  heading: string;
  description: string;
  primaryButtonLabel: string;
  primaryButtonLink: string;
  secondaryButtonLabel: string;
  secondaryButtonLink: string;
  images: string[];
};

const defaultConfig: HomeHeroConfig = {
  kicker: '',
  heading: '',
  description: '',
  primaryButtonLabel: '',
  primaryButtonLink: '/research',
  secondaryButtonLabel: '',
  secondaryButtonLink: '/members',
  images: [],
};

const AdminHomeHero: React.FC = () => {
  const [config, setConfig] = useState<HomeHeroConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get<HomeHeroConfig>('/home-hero');
      setConfig({
        ...defaultConfig,
        ...(res.data || {}),
        images: Array.isArray(res.data?.images) ? res.data.images : [],
      });
    } catch (err) {
      console.error(err);
      setError('Load failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleFieldChange = (key: keyof HomeHeroConfig, value: string) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await api.put('/home-hero', config);
      await load();
    } catch (err) {
      console.error(err);
      setError('Save failed. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const res = await api.post<{ url?: string; path?: string }>(
        '/uploads/hero',
        formData,
        { timeout: 60000 }
      );
      const rawPath = res.data.path || '';
      const base = (api.defaults.baseURL as string | undefined) || '';
      const origin = base.startsWith('http')
        ? new URL(base).origin
        : window.location.origin;
      const url = rawPath
        ? new URL(rawPath, origin).toString()
        : res.data.url || '';
      if (!url) return;
      setConfig((prev) => ({ ...prev, images: [...prev.images, url] }));
    } catch (err) {
      console.error(err);
      alert('Upload failed. Please try again later.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const moveImage = (index: number, dir: -1 | 1) => {
    setConfig((prev) => {
      const list = [...prev.images];
      const target = index + dir;
      if (target < 0 || target >= list.length) return prev;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...prev, images: list };
    });
  };

  const removeImage = (index: number) => {
    setConfig((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return <p className="text-sm text-neutral-600">Loading...</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          Homepage Hero Configuration
        </h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded-full bg-brand-red px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-white hover:bg-red-700 disabled:opacity-70"
        >
          {saving ? 'Saving...' : 'Save settings'}
        </button>
      </div>
      <p className="mb-6 text-xs text-neutral-500">
        Configure the homepage hero image, titles, and button labels. When multiple images are uploaded, the frontend rotates them automatically.
      </p>

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <div className="space-y-3 text-xs">
          <div>
            <label className="mb-1 block font-medium text-neutral-700">
              Kicker (e.g. Prof. Hui Pan's Research Group)
            </label>
            <input
              value={config.kicker}
              onChange={(e) => handleFieldChange('kicker', e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
            />
          </div>
          <div>
            <label className="mb-1 block font-medium text-neutral-700">
              Main title
            </label>
            <input
              value={config.heading}
              onChange={(e) => handleFieldChange('heading', e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
            />
          </div>
          <div>
            <ContentRichTextEditor
              label="Description"
              value={config.description}
              onChange={(html) => handleFieldChange('description', html)}
              helperText="Supports subscript/superscript and basic formatting."
            />
          </div>
        </div>

        <div className="space-y-3 text-xs">
          <div>
            <label className="mb-1 block font-medium text-neutral-700">
              Primary button label
            </label>
            <input
              value={config.primaryButtonLabel}
              onChange={(e) =>
                handleFieldChange('primaryButtonLabel', e.target.value)
              }
              className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
              placeholder="e.g. Explore Research"
            />
          </div>
          <div>
            <label className="mb-1 block font-medium text-neutral-700">
              Primary button link
            </label>
            <input
              value={config.primaryButtonLink}
              onChange={(e) =>
                handleFieldChange('primaryButtonLink', e.target.value)
              }
              className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
              placeholder="/research"
            />
          </div>
          <div>
            <label className="mb-1 block font-medium text-neutral-700">
              Secondary button label
            </label>
            <input
              value={config.secondaryButtonLabel}
              onChange={(e) =>
                handleFieldChange('secondaryButtonLabel', e.target.value)
              }
              className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
              placeholder="e.g. Meet the Team"
            />
          </div>
          <div>
            <label className="mb-1 block font-medium text-neutral-700">
              Secondary button link
            </label>
            <input
              value={config.secondaryButtonLink}
              onChange={(e) =>
                handleFieldChange('secondaryButtonLink', e.target.value)
              }
              className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
              placeholder="/members"
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between text-xs">
          <span className="font-semibold text-neutral-800">
            Hero background images (reorderable)
          </span>
          <label className="inline-flex items-center rounded-full border border-neutral-300 px-3 py-1 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 cursor-pointer">
            {uploading ? 'Uploading...' : 'Upload image'}
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
          </label>
        </div>

        {config.images.length === 0 ? (
          <p className="text-xs text-neutral-500">
            No hero image configured yet. The frontend will use the default image until you upload one or more images here.
          </p>
        ) : (
          <div className="grid gap-3 md:grid-cols-3">
            {config.images.map((url, index) => (
              <div
                key={url + index}
                className="overflow-hidden rounded-md border border-neutral-200 bg-neutral-50"
              >
                <div className="aspect-[16/9] w-full overflow-hidden bg-neutral-100">
                  <img
                    src={url}
                    alt={`hero-${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex items-center justify-between px-2 py-1.5 text-[11px] text-neutral-600">
                  <span>Image {index + 1}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveImage(index, -1)}
                      disabled={index === 0}
                      className="rounded px-1 py-0.5 hover:bg-neutral-200 disabled:opacity-30"
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      onClick={() => moveImage(index, 1)}
                      disabled={index === config.images.length - 1}
                      className="rounded px-1 py-0.5 hover:bg-neutral-200 disabled:opacity-30"
                    >
                      Move down
                    </button>
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="rounded px-1 py-0.5 text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <p className="mt-4 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default AdminHomeHero;

